import React from 'react';
import {
    Typography,
    Box,
    Card,
    CardContent,
    CardActions,
    Button,
    Grid,
    Chip,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    Alert,
    Snackbar,
    Tabs,
    Tab,
    Paper,
    TableCell,
    Checkbox
} from '@mui/material';
import {
    Refresh as RefreshIcon,
    Error as ErrorIcon,
    CheckCircle as SuccessIcon,
    HourglassEmpty as PendingIcon,
    Info as InfoIcon,
    Warning as WarningIcon
} from '@mui/icons-material';
import SortableTable from './SortableTable';
import { useScraperMonitoring, getStatusColor } from '../hooks/useScraperMonitoring';

/**
 * ScraperMonitoringDashboard
 *
 * - Adds selection checkboxes to the Execution History table so users can select multiple executions.
 * - Provides a top-level checkbox to select/deselect all visible executions.
 * - Adds a "Delete selected" button that calls scraperService.deleteJobs(ids).
 * - After deletion, the executions list is refreshed via refetch() and selection is cleared.
 * - Success and error feedback use existing snackbar mechanism; errors are also logged to console.
 */

function getStatusIcon(status) {
    switch (status) {
        case 'completed': return <SuccessIcon />;
        case 'running': return <PendingIcon />;
        case 'failed': return <ErrorIcon />;
        case 'pending': return <PendingIcon />;
        default: return <InfoIcon />;
    }
}

// Format log content for better readability
function formatLogContent(logContent) {
    if (!logContent) return 'No logs available';

    const lines = logContent.split('\n');
    return lines.map((line, index) => {
        const timestampMatch = line.match(/^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2})/);
        if (timestampMatch) {
            const timestamp = timestampMatch[1];
            const message = line.substring(timestampMatch[0].length).trim();
            return (
                <Box key={index} sx={{ mb: 0.5 }}>
                    <Typography variant="caption" color="textSecondary">
                        {timestamp}
                    </Typography>
                    <Typography variant="body2" sx={{ ml: 1 }}>
                        {message}
                    </Typography>
                </Box>
            );
        }
        return (
            <Typography key={index} variant="body2" sx={{ mb: 0.5 }}>
                {line}
            </Typography>
        );
    });
}

function ScraperMonitoringDashboard() {
    const {
        activeTab,
        setActiveTab,
        selectedExecution,
        openLogDialog,
        detailView,
        setDetailView,
        logFilters,
        setLogFilters,
        filteredSelectedExecutionLogs,
        filters,
        setFilters,
        page,
        setPage,
        pageSize,
        setPageSize,
        executionsData,
        isLoading,
        isError,
        alertsData,
        jobs,
        visibleExecutions,
        selectedIds,
        isDeleting,
        allSelected,
        handleToggleSelect,
        handleToggleSelectAll,
        handleDeleteSelected,
        liveLogs,
        isStreaming,
        logContainerRef,
        snackbar,
        handleCloseSnackbar,
        handleViewLogs,
        handleCloseLogDialog,
        handleRefresh,
        stopJob,
    } = useScraperMonitoring();

    const tableColumns = [
        {
            id: 'select',
            label: (
                <Checkbox
                    checked={allSelected}
                    onChange={handleToggleSelectAll}
                    inputProps={{ 'aria-label': 'select all executions' }}
                    size="small"
                />
            ),
            sortable: false
        },
        { id: 'id', label: 'ID' },
        { id: 'job_name', label: 'Job Name' },
        { id: 'status', label: 'Status' },
        { id: 'started_at', label: 'Started At' },
        { id: 'completed_at', label: 'Completed At' },
        { id: 'duration', label: 'Duration (s)' },
        { id: 'products_found', label: 'Products Found' },
        { id: 'actions', label: 'Actions', sortable: false },
    ];

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, }}>
                <Typography variant="h4" gutterBottom>
                    Scraper Monitoring
                </Typography>
                <IconButton onClick={handleRefresh} color="primary">
                    <RefreshIcon fontSize="large" />
                </IconButton>
            </Box>

            <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} sx={{ mb: 3 }}>
                <Tab label="Execution History" />
                <Tab label="Real-time Monitoring" />
                <Tab label="Alerts" />
            </Tabs>

            {activeTab === 1 && (
                <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            Active Executions
                        </Typography>
                    </Box>

                    {isLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : isError ? (
                        <Alert severity="error">Could not load executions.</Alert>
                    ) : (
                        <Grid container spacing={3}>
                            {executionsData?.items?.filter(exec => exec.status === 'running').map((execution) => (
                                <Grid item xs={12} md={6} lg={4} key={execution.id}>
                                    <Card>
                                        <CardContent>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                                <Typography variant="h6" component="div" noWrap>
                                                    {execution.job?.name || `Job ${execution.job_id}`}
                                                </Typography>
                                                <Chip
                                                    icon={getStatusIcon(execution.status)}
                                                    label={execution.status}
                                                    color={getStatusColor(execution.status)}
                                                    size="small"
                                                />
                                            </Box>
                                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                                Started: {new Date(execution.started_at).toLocaleString()}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Products found: {execution.products_found || 0}
                                            </Typography>
                                            {execution.metrics && (
                                                <Typography variant="body2" color="text.secondary">
                                                    Duration: {execution.metrics.duration ? `${execution.metrics.duration.toFixed(2)}s` : 'N/A'}
                                                </Typography>
                                            )}
                                        </CardContent>
                                        <CardActions>
                                            <Button
                                                variant='contained'
                                                size="small"
                                                onClick={() => handleViewLogs(execution)}
                                            >
                                                View Details
                                            </Button>
                                            <Button
                                                variant='outlined'
                                                size="small"
                                                color="error"
                                                onClick={() => stopJob(execution.id)}
                                                disabled={execution.status !== 'running'}
                                            >
                                                Stop Job
                                            </Button>
                                        </CardActions>
                                    </Card>
                                </Grid>
                            ))}

                            {executionsData?.items?.filter(exec => exec.status === 'running').length === 0 && (
                                <Grid item xs={12}>
                                    <Box sx={{ textAlign: 'center', p: 4 }}>
                                        <Typography variant="h6" color="text.secondary">
                                            No active executions
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                            Scraper jobs will appear here when they are running
                                        </Typography>
                                    </Box>
                                </Grid>
                            )}
                        </Grid>
                    )}
                </Box>
            )}

            {activeTab === 0 && (
                <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            Execution History
                        </Typography>

                        {/* Actions: Delete selected */}
                        <Box>
                            <Button
                                variant="outlined"
                                color="error"
                                onClick={handleDeleteSelected}
                                disabled={selectedIds.length === 0 || isDeleting}
                                sx={{ mr: 1 }}
                            >
                                Delete selected
                            </Button>
                            <Button variant="contained" onClick={handleRefresh}>
                                Refresh
                            </Button>
                        </Box>
                    </Box>

                    {/* Filters */}
                    <Paper sx={{ p: 2, mb: 3 }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                    <InputLabel>Job</InputLabel>
                                    <Select
                                        value={filters.jobId}
                                        onChange={(e) => setFilters({ ...filters, jobId: e.target.value })}
                                    >
                                        <MenuItem value="">All Jobs</MenuItem>
                                        {jobs.map((job) => (
                                            <MenuItem key={job.id} value={job.id}>
                                                {job.job_name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                    <InputLabel>Status</InputLabel>
                                    <Select
                                        value={filters.status}
                                        onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                                    >
                                        <MenuItem value="">All Statuses</MenuItem>
                                        <MenuItem value="completed">Completed</MenuItem>
                                        <MenuItem value="failed">Failed</MenuItem>
                                        <MenuItem value="running">Running</MenuItem>
                                        <MenuItem value="pending">Pending</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>
                    </Paper>

                    {isLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : isError ? (
                        <Alert severity="error">Could not load executions.</Alert>
                    ) : (
                        <SortableTable
                            data={visibleExecutions}
                            columns={tableColumns}
                            renderRow={(execution) => (
                                <React.Fragment key={execution.id}>
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            checked={selectedIds.includes(execution.id)}
                                            onChange={() => handleToggleSelect(execution.id)}
                                            inputProps={{ 'aria-label': `select execution ${execution.id}` }}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>{execution.id}</TableCell>
                                    <TableCell>{execution.job?.name || `Job ${execution.job_id}`}</TableCell>
                                    <TableCell>
                                        <Chip
                                            icon={getStatusIcon(execution.status)}
                                            label={execution.status}
                                            color={getStatusColor(execution.status)}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>{new Date(execution.started_at).toLocaleString()}</TableCell>
                                    <TableCell>
                                        {execution.completed_at
                                            ? new Date(execution.completed_at).toLocaleString()
                                            : 'N/A'}
                                    </TableCell>
                                    <TableCell>
                                        {execution.metrics && execution.metrics.duration
                                            ? execution.metrics.duration.toFixed(2)
                                            : 'N/A'}
                                    </TableCell>
                                    <TableCell>{execution.products_found || 0}</TableCell>
                                    <TableCell>
                                        <Button
                                            variant='contained'
                                            size="small"
                                            onClick={() => handleViewLogs(execution)}
                                            sx={{ mr: 1 }}
                                        >
                                            View Details
                                        </Button>
                                        {execution.status === 'running' && (
                                            <Button
                                                variant='outlined'
                                                size="small"
                                                color="error"
                                                onClick={() => stopJob(execution.id)}
                                            >
                                                Stop Job
                                            </Button>
                                        )}
                                    </TableCell>
                                </React.Fragment>
                            )}
                        />
                    )}

                    {/* Pagination controls */}
                    {executionsData && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                            <FormControl size="small" sx={{ minWidth: 120 }}>
                                <InputLabel>Page size</InputLabel>
                                <Select
                                    value={pageSize}
                                    label="Page size"
                                    onChange={(e) => {
                                        const newSize = Number(e.target.value);
                                        setPageSize(newSize);
                                        setPage(1); // reset to first page when page size changes
                                    }}
                                >
                                    <MenuItem value={10}>10</MenuItem>
                                    <MenuItem value={25}>25</MenuItem>
                                    <MenuItem value={50}>50</MenuItem>
                                </Select>
                            </FormControl>

                            <Typography>
                                Page {executionsData.page || page} of {executionsData.pages || Math.max(1, Math.ceil((executionsData.total || 0) / pageSize))}
                            </Typography>

                            <Box>
                                <Button
                                    onClick={() => setPage(p => Math.max(1, p - 1))}
                                    disabled={page === 1}
                                    sx={{ mr: 1 }}
                                >
                                    Previous
                                </Button>
                                <Button
                                    onClick={() => setPage(p => p + 1)}
                                    disabled={(page * pageSize) >= (executionsData.total || 0)}
                                >
                                    Next
                                </Button>
                            </Box>
                        </Box>
                    )}
                </Box>
            )}

            {activeTab === 2 && (
                <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            Alerts
                        </Typography>
                    </Box>

                    {alertsData.length > 0 ? (
                        <Grid container spacing={2}>
                            {alertsData.map((alert, index) => (
                                <Grid item xs={12} key={index}>
                                    <Alert severity="warning" icon={<WarningIcon />}>
                                        <Typography variant="subtitle1">{alert.job_name}</Typography>
                                        <Typography variant="body2">{alert.message}</Typography>
                                        <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                                            {alert.last_execution ? `Last execution: ${new Date(alert.last_execution).toLocaleString()}` : ''}
                                        </Typography>
                                    </Alert>
                                </Grid>
                            ))}
                        </Grid>
                    ) : (
                        <Box sx={{ textAlign: 'center', p: 4 }}>
                            <Typography variant="h6" color="text.secondary">
                                No active alerts
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                All scraper jobs are running normally
                            </Typography>
                        </Box>
                    )}
                </Box>
            )}

            {/* Log Dialog */}
            <Dialog open={openLogDialog} onClose={handleCloseLogDialog} maxWidth="lg" fullWidth>
                <DialogTitle>
                    Execution Details
                    {selectedExecution && (
                        <Typography variant="subtitle2" color="textSecondary">
                            Job: {selectedExecution.job?.name || `Job ${selectedExecution.job_id}`} |
                            Status: {selectedExecution.status}
                        </Typography>
                    )}
                </DialogTitle>
                <DialogContent>
                    {selectedExecution ? (
                        <Box>
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="body2" color="textSecondary">
                                    Started: {new Date(selectedExecution.started_at).toLocaleString()}
                                </Typography>
                                {selectedExecution.completed_at && (
                                    <Typography variant="body2" color="textSecondary">
                                        Completed: {new Date(selectedExecution.completed_at).toLocaleString()}
                                    </Typography>
                                )}
                                <Typography variant="body2" color="textSecondary">
                                    Products Found: {selectedExecution.products_found || 0}
                                </Typography>
                            </Box>

                            <Tabs value={detailView} onChange={(e, newValue) => setDetailView(newValue)} sx={{ mb: 2 }}>
                                <Tab label="Logs" value="logs" />
                                <Tab label="Metrics" value="metrics" />
                                <Tab label="Structured Logs" value="structured" />
                            </Tabs>

                            <Paper
                                ref={logContainerRef}
                                sx={{ p: 2, height: 600, overflow: 'auto' }}
                            >
                                {detailView === 'logs' && (
                                    <Box>
                                        {/* Log filtering controls */}
                                        <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                            <FormControl size="small">
                                                <InputLabel>Level</InputLabel>
                                                <Select
                                                    value={logFilters.level}
                                                    onChange={(e) => setLogFilters({ ...logFilters, level: e.target.value })}
                                                    sx={{ minWidth: 120 }}
                                                >
                                                    <MenuItem value="">All</MenuItem>
                                                    <MenuItem value="INFO">INFO</MenuItem>
                                                    <MenuItem value="WARNING">WARNING</MenuItem>
                                                    <MenuItem value="ERROR">ERROR</MenuItem>
                                                </Select>
                                            </FormControl>
                                            <TextField
                                                size="small"
                                                label="Search"
                                                value={logFilters.search}
                                                onChange={(e) => setLogFilters({ ...logFilters, search: e.target.value })}
                                                sx={{ minWidth: 200 }}
                                            />
                                            <Button
                                                size="small"
                                                variant="contained"
                                                onClick={() => setLogFilters({ level: '', search: '' })}
                                            >
                                                Clear
                                            </Button>
                                        </Box>

                                        {/* Filtered logs display */}
                                        {isStreaming ? (
                                            <Box>
                                                <Typography variant="caption" color="primary" sx={{ mb: 1, display: 'block' }}>
                                                    Live logs streaming...
                                                </Typography>
                                                <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 300, overflow: 'auto' }}>
                                                    {liveLogs || 'Waiting for logs...'}
                                                </pre>
                                            </Box>
                                        ) : (
                                            selectedExecution.logs ? (
                                                formatLogContent(filteredSelectedExecutionLogs)
                                            ) : (
                                                <Typography>No logs available for this execution.</Typography>
                                            )
                                        )}
                                    </Box>
                                )}

                                {detailView === 'metrics' && (
                                    <Box>
                                        {selectedExecution.metrics && Object.keys(selectedExecution.metrics).length > 0 ? (
                                            <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                                {JSON.stringify(selectedExecution.metrics, null, 2)}
                                            </pre>
                                        ) : (
                                            <Typography>No metrics available for this execution.</Typography>
                                        )}
                                    </Box>
                                )}

                                {detailView === 'structured' && (
                                    <Box>
                                        {selectedExecution.structured_logs && Object.keys(selectedExecution.structured_logs).length > 0 ? (
                                            <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                                {JSON.stringify(selectedExecution.structured_logs, null, 2)}
                                            </pre>
                                        ) : (
                                            <Typography>No structured logs available for this execution.</Typography>
                                        )}
                                    </Box>
                                )}
                            </Paper>
                        </Box>
                    ) : (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                            <CircularProgress />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button variant='contained' onClick={handleCloseLogDialog}>Close</Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default ScraperMonitoringDashboard;
